import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { CoreDirectivesModule } from '@core/directives/directives';
import { NgSelectModule } from '@ng-select/ng-select';
import { IGetCategoriesItemDto } from 'app/main/pages/categories.dto';
import { flattenCategories } from 'app/main/pages/categories.utils';
import { SelectFieldComponent } from 'app/main/pages/contract-control/editable-fields/select-field/select-field.component';
import { TextFieldComponent } from 'app/main/pages/contract-control/editable-fields/text-field/text-field.component';
import { IGroup, IField } from 'app/main/pages/settings/documenttype/document-type-settings-modal/document-type-settings.dto';
import { CascaderSelectComponent } from 'app/main/shared/shared-elements/cascader-select/cascader-select.component';
import { DynamicCategorySelectComponent } from 'app/main/shared/shared-elements/dynamic-category-select/dynamic-category-select.component';
import { SharedStoreFacade } from 'app/main/shared/shared/store/shared-store.facade';
import { CategoryFacade } from 'app/store/category.facade';
import { TranslocoRootModule } from 'app/transloco-root.module';
import { take } from 'rxjs';
import { IDocumentDetailRootDTO } from '../../../dto/document-details.dto';
import { IDocumentTypesDto } from '../../../dto/document-listing.dto';
import { DocumentTypeFieldNameEnum, IFieldTypeEnum } from 'app/main/pages/settings/documenttype/document-type-settings-modal/default-constants/document-type-settings.constants';

@Component({
  selector: 'app-top-section',
  standalone: true,
  imports: [
    CommonModule,
    CoreDirectivesModule,
    TranslocoRootModule,
    NgSelectModule,
    DynamicCategorySelectComponent,
    TextFieldComponent,
    SelectFieldComponent,
    FormsModule,
    ReactiveFormsModule,
    CascaderSelectComponent
  ],
  templateUrl: './top-section.component.html',
  styleUrl: './top-section.component.scss',
})
export class TopSectionComponent implements OnInit {

  @Output() updateDocument = new EventEmitter<IDocumentTypesDto>();

  @Input() group: IGroup;
  @Input() formGroup: import('@angular/forms').FormGroup;
  @Input() documentData: IDocumentDetailRootDTO;
  @Input() hasFullAccessToDocument: boolean;
  @Input() canEditDocument: boolean;


  public allCategories: IGetCategoriesItemDto[];
  public documentTypes$ = this._sharedStoreFacade.documentTypes$;
  private initialCategorySelections: Record<string, number[]> = {};
  private readonly removedCategoryIdsControlName = 'removed_category_ids';

  constructor(
    private _sharedStoreFacade: SharedStoreFacade,
    private _categoryFacade: CategoryFacade
  ) { }

  ngOnInit(): void {
    this.initializeFormControls();
    this.loadCategories();
  }

  ngOnChanges(changes: import('@angular/core').SimpleChanges): void {
    if (changes['formGroup'] && !changes['formGroup'].firstChange) {
      this.initializeFormControls(false);
    }
    if (changes['group'] && !changes['group'].firstChange) {
      this.reinitializeFormControls(changes['group'].previousValue);
    }
  }


  /**
   * Re-initializes form controls by removing old ones and adding new ones
   * @param oldGroup The previous group configuration to clean up
   */
  private reinitializeFormControls(oldGroup?: IGroup): void {
    const fieldsGroup = this.formGroup.get('fields') as FormGroup;
    if (fieldsGroup) {
       Object.keys(fieldsGroup.controls).forEach(key => {
         fieldsGroup.removeControl(key);
       });
    }

    // Remove old controls from the root formGroup based on the previous group definition
    if (oldGroup) {
      oldGroup.fields.forEach(field => {
         if (this.formGroup.contains(field.field_name)) {
           this.formGroup.removeControl(field.field_name);
         }
      });
    }

    // Now initialize new ones
    this.initializeFormControls();
  }



  /**
   * Initializes form controls based on group fields configuration
   */
  private initializeFormControls(forceUpdate: boolean = true): void {
    const fieldsGroup = this.ensureFieldsGroupExists();
    this.captureInitialCategorySelections();
    this.ensureRemovedCategoryIdsControl();

    this.group.fields.forEach((field) => {
      this.addFieldControl(field, forceUpdate);
      this.addIndexFieldControl(field, fieldsGroup, forceUpdate);
    });

    this.updateRemovedCategoriesControl();
  }

  /**
   * Ensures the 'fields' FormGroup exists in the parent FormGroup
   * @returns The fields FormGroup
   */
  private ensureFieldsGroupExists(): FormGroup {
    if (!this.formGroup.contains('fields')) {
      this.formGroup.addControl('fields', new FormGroup({}));
    }
    return this.formGroup.get('fields') as FormGroup;
  }



  /**
   * Adds a form control for default and document_category field types
   * @param field The field configuration
   */
  private addFieldControl(field: IField, forceUpdate: boolean): void {
    const isDefaultField = field.type === IFieldTypeEnum.DEFAULT;
    const isRootCategoryField = field.type === IFieldTypeEnum.DOCUMENT_CATEGORY && !field.category?.parent_id;
    if (isDefaultField || isRootCategoryField) {
      const canEdit = this.hasFullAccessToDocument || this.canEditDocument;
      
      if (this.formGroup.contains(field.field_name)) {
        const control = this.formGroup.get(field.field_name);
        if (forceUpdate) {
          control.setValue(field.value ?? null);
        }
        // Update disabled state based on permissions
        if (canEdit) {
          control.enable();
        } else {
          control.disable();
        }
      } else {
        this.formGroup.addControl(
          field.field_name,
          new FormControl(
            { value: field.value ?? null, disabled: !canEdit },
            this.getValidator(field)
          ),
        );
      }
    }
  }

  private captureInitialCategorySelections(): void {
    this.initialCategorySelections = {};
    this.group.fields
      .filter(field => field.type === IFieldTypeEnum.DOCUMENT_CATEGORY)
      .forEach((field) => {
        const initialValues = Array.isArray(field.value) ? (field.value as unknown[]) : [];
        this.initialCategorySelections[field.field_name] = initialValues
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id));
      });
  }

  private ensureRemovedCategoryIdsControl(): void {
    if (!this.formGroup.contains(this.removedCategoryIdsControlName)) {
      this.formGroup.addControl(this.removedCategoryIdsControlName, new FormControl([]));
    }
  }

  private updateRemovedCategoriesControl(): void {
    const removedIds = new Set<number>();

    this.group.fields
      .filter(field => field.type === IFieldTypeEnum.DOCUMENT_CATEGORY)
      .forEach((field) => {
        const initialIds = this.initialCategorySelections[field.field_name] ?? [];
        const currentIds = this.formGroup.get(field.field_name)?.value ?? [];
        const normalizedCurrentIds = Array.isArray(currentIds)
          ? (currentIds as unknown[]).map((id) => Number(id)).filter((id) => Number.isFinite(id))
          : [];
        const currentIdsSet = new Set<number>(
          normalizedCurrentIds
        );

        initialIds.forEach((id) => {
          if (!currentIdsSet.has(id)) {
            removedIds.add(id);
          }
        });
      });

    this.formGroup.get(this.removedCategoryIdsControlName)?.setValue([...removedIds]);
  }




  private getValidator(field: IField): ValidatorFn | null {
    return field.field_name === DocumentTypeFieldNameEnum.DocumentType ? Validators.required : null;
  }


  /**
   * Adds a form control for index_field type to the fields FormGroup
   * @param field The field configuration
   * @param fieldsGroup The fields FormGroup
   */
  private addIndexFieldControl(field: IField, fieldsGroup: FormGroup, forceUpdate: boolean): void {
    if (field.type === 'index_field') {
      if (fieldsGroup.contains(field.field_name)) {
        if (forceUpdate) {
          fieldsGroup.get(field.field_name).setValue(field.value);
        }
      } else {
        fieldsGroup.addControl(
          field.field_name,
          new FormControl(field.value)
        );
      }
    }
  }

  /**
   * Loads and flattens the categories from the store
   */
  private loadCategories(): void {
    this._categoryFacade.categories$
      .pipe(take(1))
      .subscribe((categories) => {
        this.allCategories = flattenCategories(structuredClone(categories));
      });
  }



  /**
   * Updates the document type in the form group and emits an update event
   * @param documentType The document type to update
   * @returns void
   */
  updateDocumentType(documentType: IDocumentTypesDto): void {
    const documentTypeId = documentType?.id;
    if (!documentTypeId) {
      console.warn('Document Type ID is missing.');
      return;
    }

    this.formGroup.get('document_type')?.setValue(documentTypeId);
    this.updateDocument.emit(documentType);
  }



  /**
   * Updates the selected categories in the form group
   * @param $event The selected category IDs
   * @param idx The index of the category field
   * @returns void
   */
  selectedCategoriesChange($event: number[], idx: number): void {
    const categoryField = this.group.fields.find((f, index: number) => f.type === 'document_category' && index === idx);
    if (categoryField) {
      const control = this.formGroup.get(categoryField.field_name);
      if (control) {
        control.setValue($event);
      }
    }
    this.updateRemovedCategoriesControl();
    this.updateDocument.emit(null);
  }




  /**
   * Updates the value of a field in the form group
   * @param $event The new value
   * @param field The field configuration
   * @returns void
   */
  valueChange($event: string | number, field: IField): void {
    const control = this.formGroup.get('fields').get(field.field_name);
    if (control) {
      control.setValue($event);
    }
    this.updateDocument.emit(null);
  }
}
